import React, { useState } from 'react';
import Button, { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTransition,
} from '@atlaskit/modal-dialog';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { messages } from './messages';
import { Radio } from '@atlaskit/radio';
import TextArea from '@atlaskit/textarea';
import { completeChurnSurvey } from '../../duck/actions/accountId';
import { Dimensions } from '../../../../components/visuals';
import { StyledGreyParagraph } from '../styledComponents';

const TextfieldWrapper = styled.div`
  margin-left: ${Dimensions.units(3.5)}px;
  margin-top: ${Dimensions.units(0.625)}px;
  width: 80%;
`;

const StyledDiv = styled.div`
  margin-bottom: ${Dimensions.units(0.625)}px;
`;

const StyledTextArea = styled(TextArea)`
  height: ${Dimensions.units(12.5)}px;
`;

const reasonsCancellingSubscription = (formatMessage) => {
  return {
    ask: {
      placeholder: formatMessage(messages.billDeactivateReasonsCancellingSubscriptionAsk),
    },
  };
};

const reasonsNewSolution = (formatMessage) => {
  return {
    ask: {
      placeholder: formatMessage(messages.billDeactivateSubscriptionReasonsNewSolutionAsk),
    },
  };
};

const covid = (formatMessage) => {
  return {
    ask: {
      placeholder: formatMessage(messages.billDeactivateCovidAsk),
    },
  };
};

const getOptions = (intl) => {
  const { formatMessage } = intl;
  const options = [
    {
      key: 'closed',
      label: <FormattedMessage {...messages.billDeactivateOptionsClosed} />,
      defaultMessage: messages.billDeactivateOptionsClosed.defaultMessage,
      nested: reasonsCancellingSubscription(formatMessage),
    },
    {
      key: 'project-based',
      label: <FormattedMessage {...messages.billDeactivateOptionsProjectBased} />,
      defaultMessage: messages.billDeactivateOptionsProjectBased.defaultMessage,
      nested: reasonsCancellingSubscription(formatMessage),
    },
    {
      key: 'not-meeting-needs',
      label: <FormattedMessage {...messages.billDeactivateOptionsNotMeetingNeeds} />,
      defaultMessage: messages.billDeactivateOptionsNotMeetingNeeds.defaultMessage,
      nested: reasonsCancellingSubscription(formatMessage),
    },
    {
      key: 'cost',
      label: <FormattedMessage {...messages.billDeactivateOptionsCost} />,
      defaultMessage: messages.billDeactivateOptionsCost.defaultMessage,
      nested: reasonsCancellingSubscription(formatMessage),
    },
    {
      key: 'moving-to-server-or-dc',
      label: <FormattedMessage {...messages.billDeactivateOptionsMovingToServerDC} />,
      defaultMessage: messages.billDeactivateOptionsMovingToServerDC.defaultMessage,
      nested: reasonsCancellingSubscription(formatMessage),
    },
    {
      key: 'merging',
      label: <FormattedMessage {...messages.billDeactivateOptionsMerging} />,
      defaultMessage: messages.billDeactivateOptionsMerging.defaultMessage,
      nested: reasonsCancellingSubscription(formatMessage),
    },
    {
      key: 'post-evaluation',
      label: <FormattedMessage {...messages.billDeactivateOptionsPostEvaluation} />,
      defaultMessage: messages.billDeactivateOptionsPostEvaluation.defaultMessage,
      nested: reasonsCancellingSubscription(formatMessage),
    },
    {
      key: 'sandbox',
      label: <FormattedMessage {...messages.billDeactivateOptionsSandbox} />,
      defaultMessage: messages.billDeactivateOptionsSandbox.defaultMessage,
      nested: reasonsCancellingSubscription(formatMessage),
    },
    {
      key: 'resetting',
      label: <FormattedMessage {...messages.billDeactivateOptionsResetting} />,
      defaultMessage: messages.billDeactivateOptionsResetting.defaultMessage,
      nested: reasonsCancellingSubscription(formatMessage),
    },
    {
      key: 'competitor',
      label: <FormattedMessage {...messages.billDeactivateOptionsCompetitorPlural} />,
      defaultMessage: messages.billDeactivateOptionsCompetitorPlural.defaultMessage,
      nested: reasonsNewSolution(formatMessage),
    },
    {
      key: 'covid',
      label: <FormattedMessage {...messages.billDeactivateOptionsCovid} />,
      defaultMessage: messages.billDeactivateOptionsCovid.defaultMessage,
      nested: covid(formatMessage),
    },
    {
      key: 'other',
      label: <FormattedMessage {...messages.billDeactivateOptionsOther} />,
      defaultMessage: messages.billDeactivateOptionsOther.defaultMessage,
      nested: reasonsCancellingSubscription(formatMessage),
    },
  ];
  return options;
};

interface Props {
  accountDetails: any;
  closeModal: () => void;
}

const CancelSurveyModalBody = ({
  options,
  selectedOption,
  setSelectedOption,
  setComment,
  setIsDisabledButton,
}) => {
  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
    setIsDisabledButton(true);
  };

  const handleCommentChange = (event) => {
    const comment = event.target.value;
    setComment(comment);
    if (comment) {
      setIsDisabledButton(false);
    } else {
      setIsDisabledButton(true);
    }
  };

  return (
    <>
      <ModalBody>
        <p>
          <b>
            <FormattedMessage {...messages.subscriptionTakeCareMessage} />
          </b>
        </p>
        <p>
          <b>
            <FormattedMessage {...messages.cancelingReasonQuestion} />
          </b>
          <StyledGreyParagraph>
            <FormattedMessage {...messages.selectOneMessage} />
          </StyledGreyParagraph>
          <p>
            {options.map((option) => (
              <StyledDiv key={option.key}>
                <Radio
                  isInvalid={false}
                  onChange={handleOptionChange}
                  isChecked={option.key === selectedOption}
                  name="product"
                  value={option.key}
                  label={option.label}
                />
                {selectedOption === option.key && (
                  <TextfieldWrapper>
                    <StyledTextArea
                      data-cy="churn-survey-comment"
                      placeholder={option.nested.ask.placeholder}
                      onChange={handleCommentChange}
                    />
                  </TextfieldWrapper>
                )}
              </StyledDiv>
            ))}
          </p>
        </p>
      </ModalBody>
    </>
  );
};

const CancelSurveyModal = (props: Props) => {
  const dispatch = useDispatch();
  const [isDisabledButton, setIsDisabledButton] = useState<boolean>(true);
  const [selectedOption, setSelectedOption] = useState('');
  const [comment, setComment] = useState('');

  const intl = useIntl();
  const options = getOptions(intl);

  const handleSubmitSurvey = () => {
    const optionObject = options.find((item) => item.key === selectedOption);
    const message = optionObject && optionObject.defaultMessage ? optionObject.defaultMessage : '';
    dispatch(
      completeChurnSurvey(
        props.accountDetails.id,
        message,
        selectedOption,
        comment,
        props.closeModal,
      ),
    );
  };

  return (
    <ModalTransition>
      <Modal width="large">
        <ModalHeader>
          <ModalTitle appearance="danger">
            <FormattedMessage {...messages.subscriptionCancelled} />
          </ModalTitle>
          <IconButton
            appearance="subtle"
            icon={CrossIcon}
            label="Close Modal"
            onClick={props.closeModal}
          />
        </ModalHeader>
        <CancelSurveyModalBody
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          setComment={setComment}
          options={options}
          setIsDisabledButton={setIsDisabledButton}
        />
        <ModalFooter>
          <Button
            isDisabled={isDisabledButton}
            data-cy="submit-survey"
            appearance="primary"
            onClick={handleSubmitSurvey}
          >
            <FormattedMessage {...messages.submitSurveyButton} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalTransition>
  );
};

export default CancelSurveyModal;
